import { defaultLeaveOf } from '@/constant/hr/leave-rule-catalog';
import { dateOnlyOf } from '@/lib/utils/custom-date/custom-date';
import { DatedWorkDay, hrTermOn } from '@/lib/utils/work-days/work-days';
import {
  LeaveSheetAllotment,
  LeaveSheetHoliday,
  LeaveSheetLeave,
  LeaveSheetPolicy,
  LeaveSheetToil,
} from '../types/leave-sheet.types';
import { holidayDaysOf, leaveDaysOf } from './leave-days';
import { isOvertimeLeave } from './overtime-leave';

/** The HR term fields the allotment balance reads. */
export interface AllotmentTerm {
  HRPeriodStartDate?: string | null;
  HRPeriodEndDate?: string | null;
  IsCurrentHrYear?: boolean | null;
  EmploymentType?: string;
  LeaveCarryForwardDays?: number;
  ToilCarryForwardDays?: number;
  SpecialToilCarryForwardDays?: number;
  WorkDays?: DatedWorkDay[];
  LeaveAllotments?: LeaveSheetAllotment[];
}

export interface AllotmentBalance {
  /** The allotment, the default leave's carry-forwards and approved TOIL included. */
  limit: number;
  /** Days of the category's Pending and Approved requests in the window, counted now. */
  used: number;
  remaining: number;
  isDefault: boolean;
}

/** The days ("YYYY-MM-DD", both inclusive) whose requests count against an allotment. */
export interface AllotmentWindow<T> {
  term: T | undefined;
  start: string;
  end: string;
}

const DAY_MS = 24 * 60 * 60 * 1000;

/** Calendar-day arithmetic on plain "YYYY-MM-DD" days (date only, never zone-converted). */
const toMs = (day: string) => {
  const [year, month, date] = day.split('-').map(Number);
  return Date.UTC(year, month - 1, date);
};
const addDays = (day: string, count: number) =>
  new Date(toMs(day) + count * DAY_MS).toISOString().slice(0, 10);
/** `day` one year later; 29 February becomes 28 February (the workflow's `yearAfter`). */
const yearAfter = (day: string) =>
  `${Number(day.slice(0, 4)) + 1}${day.slice(4) === '-02-29' ? '-02-28' : day.slice(4)}`;

/**
 * The HR term whose allotments a leave starting on `day` counts against, and the days whose requests count
 * (the workflow's `allotmentTermOn`, blocks/workflows/leave/lib/leave.js): the term holding the day; after the
 * current term without a next one on file, the current term over the year after it; else `hrTermOn`'s term.
 */
export const allotmentTermOn = <T extends AllotmentTerm>(
  terms: readonly T[],
  day: string
): AllotmentWindow<T> => {
  const holding = terms.find((term) => {
    const start = dateOnlyOf(term.HRPeriodStartDate);
    const end = dateOnlyOf(term.HRPeriodEndDate);
    return Boolean(start && end && start <= day && day <= end);
  });
  if (holding) {
    return {
      term: holding,
      start: dateOnlyOf(holding.HRPeriodStartDate),
      end: dateOnlyOf(holding.HRPeriodEndDate),
    };
  }
  const current = terms.find((term) => term.IsCurrentHrYear === true);
  const currentEnd = dateOnlyOf(current?.HRPeriodEndDate);
  if (current && currentEnd && day > currentEnd) {
    return { term: current, start: addDays(currentEnd, 1), end: yearAfter(currentEnd) };
  }
  const term = hrTermOn(terms, day);
  return {
    term,
    start: term ? dateOnlyOf(term.HRPeriodStartDate) : day,
    end: term ? dateOnlyOf(term.HRPeriodEndDate) : day,
  };
};

/**
 * What is left of one category's allotment, the same count LEAVE_REQUEST_SUBMIT refuses on
 * (blocks/workflows/leave/lib/leave.js `allotmentBalanceOf`): `LeaveLimitTotalDays`, for the term's default
 * leave plus `LeaveCarryForwardDays` and, under a TOIL rule, the TOIL carried forward and approved TOIL up to
 * `lastDay`; minus the Pending and Approved requests of the category (`usage`), each counted now in its working
 * days inside the window (`leaveDaysOf`). Missed attendance, which the leave sheet also charges to the
 * default leave, is not counted. `null` when the category has no limit (none in the term, unlimited, or the
 * "Overtime Leave", paid from the overtime balance).
 */
export const allotmentBalanceOf = ({
  window,
  categoryId,
  usage,
  toils,
  policy,
  lastDay,
  holidays = [],
}: {
  window: AllotmentWindow<AllotmentTerm>;
  categoryId: string;
  usage: Pick<LeaveSheetLeave, 'StartDate' | 'EndDate' | 'LeaveType'>[];
  toils: LeaveSheetToil[];
  policy: Pick<LeaveSheetPolicy, 'toil'>;
  lastDay: string;
  holidays?: LeaveSheetHoliday[];
}): AllotmentBalance | null => {
  const term = window.term;
  if (!term) return null;
  const allotments = term.LeaveAllotments ?? [];
  const allotment = allotments.find((item) => item.LeaveCategoryId === categoryId);
  if (!allotment || allotment.IsUnlimited || isOvertimeLeave(allotment.LeaveCategory)) return null;
  const isDefault = defaultLeaveOf(allotments, term.EmploymentType)?.LeaveCategoryId === categoryId;
  let limit =
    (allotment.LeaveLimitTotalDays ?? 0) + (isDefault ? (term.LeaveCarryForwardDays ?? 0) : 0);
  if (isDefault && policy.toil) {
    limit += (term.ToilCarryForwardDays ?? 0) + (term.SpecialToilCarryForwardDays ?? 0);
    for (const toil of toils) {
      if (dateOnlyOf(toil.ToilDate) <= lastDay) limit += toil.ToilType === 'FullDay' ? 1 : 0.5;
    }
  }
  const holidayDays = holidayDaysOf(holidays);
  const used = usage.reduce(
    (sum, leave) => sum + leaveDaysOf(leave, term.WorkDays ?? [], holidayDays, window),
    0
  );
  return { limit, used, remaining: limit - used, isDefault };
};
